import { type ComponentType, lazy } from "react";

/**
 * Marks that we have already reloaded the tab to recover a failed chunk, so a
 * genuinely broken deploy surfaces as an error instead of a reload loop.
 */
const RELOAD_FLAG = "equility:chunk-reload";

type Factory<T> = () => Promise<{ default: T }>;

function readFlag(): boolean {
	try {
		return window.sessionStorage.getItem(RELOAD_FLAG) === "1";
	} catch {
		// Storage is unavailable (private mode, blocked cookies). Treat it as
		// "already tried" so we never reload without being able to record it.
		return true;
	}
}

function writeFlag(value: boolean) {
	try {
		if (value) {
			window.sessionStorage.setItem(RELOAD_FLAG, "1");
		} else {
			window.sessionStorage.removeItem(RELOAD_FLAG);
		}
	} catch {
		// Ignore: failing to persist the flag only costs us the loop guard.
	}
}

/**
 * Wraps a dynamic-import factory so a chunk that disappeared under a long-lived
 * tab recovers instead of white-screening.
 *
 * When a deploy replaces the content-hashed files in /static/js, a tab still
 * running the previous build asks for a chunk name that no longer exists. The
 * SPA fallback answers with index.html, so the browser parses HTML as JavaScript
 * and throws "SyntaxError: Unexpected token '<'" while webpack rejects the
 * import. Reloading picks up the current asset manifest.
 */
export function withChunkRetry<T>(factory: Factory<T>): Factory<T> {
	return async () => {
		try {
			const mod = await factory();
			writeFlag(false);
			return mod;
		} catch (error) {
			if (readFlag()) throw error;
			writeFlag(true);
			window.location.reload();
			// The reload tears the document down; never resolve so React does not
			// render a half-initialised tree in the meantime.
			return new Promise<{ default: T }>(() => {});
		}
	};
}

/** Drop-in replacement for React.lazy that survives a mid-session deploy. */
export function lazyWithRetry<T extends ComponentType<any>>(
	factory: Factory<T>,
) {
	return lazy(withChunkRetry(factory));
}
